/** Trigger dialogs that can start an OAuth connection from inside themselves. */
export type TTriggerConnectionIntentKind = 'webhook' | 'poll' | 'agent-webhook';

/**
 * What a trigger dialog needs to put itself back on screen after the browser
 * has been away at an OAuth provider.
 */
export interface ITriggerConnectionIntent {
  kind: TTriggerConnectionIntentKind;
  /** Router pathname the dialog was open on; the resume only fires there. */
  pathname: string;
  integrationName: string;
  integrationVersion?: string;
  /** Epoch ms, used to drop intents the user never came back from. */
  createdAt: number;
}

const STORAGE_KEY = 'trigger-connection-intent';

/** Past this, assume the user abandoned the OAuth flow and start clean. */
const MAX_AGE_MS = 30 * 60 * 1000;

const isIntentKind = (value: unknown): value is TTriggerConnectionIntentKind =>
  value === 'webhook' || value === 'poll' || value === 'agent-webhook';

/**
 * Reads the pending trigger-connection intent.
 *
 * @returns The stored intent, or null when missing, malformed or expired
 */
export function readTriggerConnectionIntent(): ITriggerConnectionIntent | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<ITriggerConnectionIntent>;
    if (
      !isIntentKind(parsed.kind) ||
      !parsed.pathname ||
      !parsed.integrationName ||
      typeof parsed.createdAt !== 'number'
    ) {
      return null;
    }
    if (Date.now() - parsed.createdAt > MAX_AGE_MS) return null;

    return {
      kind: parsed.kind,
      pathname: parsed.pathname,
      integrationName: parsed.integrationName,
      integrationVersion: parsed.integrationVersion,
      createdAt: parsed.createdAt,
    };
  } catch {
    return null;
  }
}

/**
 * Stores the intent of a trigger dialog that is about to hand the browser to an
 * OAuth provider. Must be called before the redirect, not after.
 *
 * @param intent - Intent to store, minus its timestamp
 */
export function writeTriggerConnectionIntent(
  intent: Omit<ITriggerConnectionIntent, 'createdAt'>,
): void {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...intent, createdAt: Date.now() }),
    );
  } catch {
    // Storage unavailable (private mode, quota). The OAuth flow still works,
    // it just won't resume the dialog, which is the behaviour without this.
  }
}

/** Drops the pending intent. Safe to call when there is none. */
export function clearTriggerConnectionIntent(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // See writeTriggerConnectionIntent.
  }
}

/**
 * Whether a trigger dialog on this page is waiting on an OAuth return, so the
 * other consumers of the `?data=` callback param leave that connection alone.
 *
 * @param pathname - Router pathname of the caller
 * @returns True while an intent for this page is pending
 */
export function hasPendingTriggerConnection(pathname: string): boolean {
  return readTriggerConnectionIntent()?.pathname === pathname;
}

/**
 * Drops an intent for this page that no callback is coming for: the user
 * abandoned the sign-in and came back by hand. Left alone it would claim the
 * next unrelated OAuth return here. An intent written by the current document
 * is kept, so a redirect still in flight survives a re-render.
 *
 * @param pathname - Router pathname of the caller
 */
export function clearStaleTriggerConnectionIntent(pathname: string): void {
  const intent = readTriggerConnectionIntent();
  if (
    intent?.pathname === pathname &&
    intent.createdAt < performance.timeOrigin
  ) {
    clearTriggerConnectionIntent();
  }
}

/**
 * Takes the pending intent if it belongs to this dialog on this page, clearing
 * it so only the first caller resumes.
 *
 * @param kind - Trigger dialog asking to resume
 * @param pathname - Router pathname the caller is rendered on
 * @returns The intent when it matches, otherwise null
 */
export function consumeTriggerConnectionIntent(
  kind: TTriggerConnectionIntentKind,
  pathname: string,
): ITriggerConnectionIntent | null {
  const intent = readTriggerConnectionIntent();
  if (!intent || intent.kind !== kind || intent.pathname !== pathname) {
    return null;
  }
  clearTriggerConnectionIntent();
  return intent;
}
